/**
 * Plugin for the `assignment` module kind (F3-B) — homework/exam/project/participation.
 *
 * Registers in the CourseModuleRegistry WITHOUT touching the core (same
 * recipe as lesson/resource). The CONTENT (title, instructions, rubric,
 * submission type) lives in the `assignment` row (ref_id); SUBMISSIONS and
 * GRADES are per-cohort (`assignment_submission`/`assignment_grade`) and feed
 * the gradebook, NOT the course's lesson completion — that's why
 * `completion/gradeContribution` return `null` (weight decided in the cohort's gradebook).
 */

import type { FeatureManifest } from "../country/packs";
import {
  anyObjectValidator,
  type CourseModulePlugin,
  isPlainObject,
  type ModuleValidationResult,
} from "./types";

export type AssignmentKind = "homework" | "exam" | "project" | "participation";
export const ASSIGNMENT_KINDS: readonly AssignmentKind[] = [
  "homework",
  "exam",
  "project",
  "participation",
];

export type SubmissionType = "file" | "text" | "none";
export const SUBMISSION_TYPES: readonly SubmissionType[] = ["file", "text", "none"];

// ─── Rubric ─────────────────────────────────────────────────────────────────────

export interface RubricLevel {
  label: string;
  points: number;
}
export interface RubricCriterion {
  name: string;
  description?: string;
  levels: RubricLevel[];
}
export interface Rubric {
  criteria: RubricCriterion[];
}

export function isAssignmentKind(v: unknown): v is AssignmentKind {
  return typeof v === "string" && (ASSIGNMENT_KINDS as readonly string[]).includes(v);
}
export function isSubmissionType(v: unknown): v is SubmissionType {
  return typeof v === "string" && (SUBMISSION_TYPES as readonly string[]).includes(v);
}

/**
 * Validates (and normalizes) a free-form rubric. Tolerant: discards
 * malformed levels/criteria instead of failing (v1). Returns `null` if there is no usable rubric.
 */
export function normalizeRubric(value: unknown): Rubric | null {
  if (!isPlainObject(value)) {
    return null;
  }
  const rawCriteria = value.criteria;
  if (!Array.isArray(rawCriteria)) {
    return null;
  }
  const criteria: RubricCriterion[] = [];
  for (const c of rawCriteria) {
    if (!isPlainObject(c) || typeof c.name !== "string" || c.name.trim() === "") {
      continue;
    }
    const levels: RubricLevel[] = [];
    if (Array.isArray(c.levels)) {
      for (const l of c.levels) {
        if (!isPlainObject(l)) {
          continue;
        }
        const label = typeof l.label === "string" ? l.label : "";
        const points = typeof l.points === "number" ? l.points : Number(l.points);
        if (label.trim() === "" || Number.isNaN(points)) {
          continue;
        }
        levels.push({ label, points });
      }
    }
    criteria.push({
      name: c.name,
      description: typeof c.description === "string" ? c.description : undefined,
      levels,
    });
  }
  return criteria.length > 0 ? { criteria } : null;
}

/** Sum of the rubric's max points (highest `points` per criterion). */
export function rubricMaxPoints(rubric: Rubric | null): number {
  if (!rubric) {
    return 0;
  }
  return rubric.criteria.reduce((total, c) => {
    const best = c.levels.reduce((m, l) => Math.max(m, l.points), 0);
    return total + best;
  }, 0);
}

/** Sum of points of a rubric selection ({criterion: points}). */
export function rubricScoreTotal(scores: Record<string, number> | null | undefined): number {
  if (!scores) {
    return 0;
  }
  return Object.values(scores).reduce((sum, p) => sum + (typeof p === "number" ? p : 0), 0);
}

// ─── Manifest + plugin ─────────────────────────────────────────────────────────

export const ASSIGNMENT_MANIFEST: FeatureManifest = {
  key: "academy.module.assignment",
  name: "Tarea evaluable",
  description:
    "Tarea, examen, proyecto o participación con rúbrica opcional, entrega (archivo/texto) y calificación por cohorte que alimenta el libro de notas.",
  scope: "course",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "profesor",
  implications: {
    navHidden: ["Paradas «Tarea» en Aprender", "«Tarea» en el menú de módulos"],
    flowsBlocked: ["Entregar tareas (texto/archivo)"],
    dataPolicy: "preserved",
  },
};

export function assignmentModuleManifest(): FeatureManifest {
  return ASSIGNMENT_MANIFEST;
}

export function createAssignmentModulePlugin<TComponent = unknown>(
  components: {
    authoringComponent?: () => Promise<TComponent>;
    playerComponent?: () => Promise<TComponent>;
  } = {},
): CourseModulePlugin<TComponent> {
  return {
    kind: "assignment",
    manifest: ASSIGNMENT_MANIFEST,
    // The course_module config for assignment is free-form (detail in `assignment`).
    configSchema: anyObjectValidator(),
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    // The assignment does NOT contribute to the course's LESSON completion/
    // grade: its weight is decided in the COHORT's gradebook (grade_item → assignment).
    completionContribution: (): number | null => null,
    gradeContribution: (): number | null => null,
  };
}

/** Validates an assignment config (always an object; v1 has no required fields). */
export function validateAssignmentConfig(
  value: unknown,
): ModuleValidationResult<Record<string, unknown>> {
  return anyObjectValidator().parse(value);
}
